import { FC } from "react";
import { Brain, Zap, Layers, Clock } from "lucide-react";

interface ShredCreatingProps {
    refetch: () => void;
    time?: number; // Keep for compatibility but won't use
}

const ShredCreating: FC<ShredCreatingProps> = () => {
    return (
        <div className="relative overflow-hidden rounded-2xl min-h-[400px] bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10 flex flex-col border border-indigo-300 shadow-lg">
            {/* Enhanced AI Background Effects */}
            <div className="absolute inset-0 overflow-hidden">
                {/* Animated gradient orbs */}
                <div className="absolute top-8 right-8 w-20 h-20 bg-gradient-to-br from-indigo-400/30 to-purple-400/30 rounded-full blur-xl animate-pulse"></div>
                <div className="absolute bottom-8 left-8 w-16 h-16 bg-gradient-to-br from-purple-400/30 to-pink-400/30 rounded-full blur-xl animate-pulse delay-1000"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full blur-2xl animate-pulse delay-500"></div>

                {/* Floating particles */}
                <div className="absolute top-12 left-12 w-2 h-2 bg-indigo-400 rounded-full animate-bounce delay-200"></div>
                <div className="absolute top-20 right-16 w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce delay-700"></div>
                <div className="absolute bottom-16 right-12 w-2.5 h-2.5 bg-pink-400 rounded-full animate-bounce delay-1200"></div>
            </div>

            <div className="relative z-10 flex flex-col justify-center items-center py-16 px-8 h-full">
                <div className="text-center space-y-8 w-full max-w-4xl">
                    {/* AI Icon with subtle glow */}
                    <div className="relative">
                        <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-xl"></div>
                        <div className="relative bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full p-4 mx-auto w-fit">
                            <Layers className="w-12 h-12 text-white animate-spin" />
                        </div>
                    </div>

                    {/* Clean title */}
                    <div className="space-y-4">
                        <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                            Generating Shreds
                        </h2>
                        <p className="text-slate-600 text-lg">
                            We are generating all the Shreds specifications based on your architecture
                        </p>

                        {/* Time estimate message */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200 max-w-md mx-auto">
                            <div className="flex items-center justify-center gap-2 mb-2">
                                <Clock className="w-4 h-4 text-blue-600" />
                                <span className="text-sm font-medium text-blue-800">Processing Time</span>
                            </div>
                            <p className="text-xs text-blue-700 leading-relaxed">
                                This operation can take up to 7 minutes depending on your service complexity
                            </p>
                        </div>
                    </div>

                    {/* Simple progress bar */}
                    <div className="w-full max-w-xs mx-auto">
                        <div className="bg-white/50 rounded-full h-2">
                            <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full animate-pulse"></div>
                        </div>
                    </div>

                    {/* Status badges */}
                    <div className="flex flex-wrap justify-center gap-4 mt-8">
                        <div className="flex items-center gap-3 bg-white/70 backdrop-blur-sm px-6 py-3 rounded-lg border border-indigo-200 min-w-fit">
                            <Brain className="w-5 h-5 text-indigo-600 flex-shrink-0 animate-pulse" />
                            <span className="text-sm font-medium text-indigo-800 whitespace-nowrap">Analyzing Architecture</span>
                        </div>
                        <div className="flex items-center gap-3 bg-white/70 backdrop-blur-sm px-6 py-3 rounded-lg border border-purple-200 min-w-fit">
                            <Layers className="w-5 h-5 text-purple-600 flex-shrink-0 animate-pulse delay-200" />
                            <span className="text-sm font-medium text-purple-800 whitespace-nowrap">Creating Specifications</span>
                        </div>
                        <div className="flex items-center gap-3 bg-white/70 backdrop-blur-sm px-6 py-3 rounded-lg border border-pink-200 min-w-fit">
                            <Zap className="w-5 h-5 text-pink-600 flex-shrink-0 animate-pulse delay-500" />
                            <span className="text-sm font-medium text-pink-800 whitespace-nowrap">Finalizing Shreds</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Hover glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 opacity-100 rounded-2xl pointer-events-none"></div>
        </div>
    );
};

export default ShredCreating;